import { Button, Card, Grid, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ConfirmationModal from '../Modals/ConfirmationModal';

const FacebookDisconnectButton = ({ facebookAccount, setFacebookAccount, setTab }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Synchronous in-flight guard. `loading` drives the UI (disabled button /
  // spinner), but it updates asynchronously, so two clicks in the same tick
  // would both read the pre-render value and fire duplicate requests. The ref
  // flips synchronously and blocks that race.
  const inFlightRef = useRef(false);

  const toggleConfirmationModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (facebookAccount.adAccountId === null) {
      logout();
    } else {
      setConfirmDisconnectOpen(!confirmDisconnectOpen);
    }
  };

  const logout = async () => {
    // Guard against a double-submit: without it a second click (button or modal
    // Confirm) fires a duplicate disconnect request while the first is in flight.
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/logout`, {
        method: 'POST',
      });
      setFacebookAccount(null);
      setTab('facebook');
    } catch (err) {
      // Surface failures via a toast rather than the modal's inline `error`
      // prop: the toast is more visible and the modal closes on failure anyway.
      notifications.show({
        title: 'Failed to disconnect Facebook',
        message: getErrorMessage(err, 'Could not disconnect Facebook from Releva. Please try again or contact support.'),
        color: 'danger',
      });
    } finally {
      // Reset UI state unconditionally. On the success path the parent unmounts
      // this component (account cleared → fresh-login flow), so these become
      // harmless no-ops there — React 18+ does not warn on setState after
      // unmount — and running them unconditionally keeps the modal/loading
      // correct even if a future refactor keeps this component mounted after a
      // disconnect, rather than coupling correctness to that unmount.
      inFlightRef.current = false;
      setLoading(false);
      setConfirmDisconnectOpen(false);
    }
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Connection</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Button variant="filled" onClick={toggleConfirmationModal} loading={loading}>
              Disconnect Facebook from Releva
            </Button>
          </Grid.Col>
        </Grid>
      </Card.Section>
      {/* `loading` disables Confirm + Close and blocks dismissal while the
          request is in flight. The modal's inline `error` prop is intentionally
          not passed — failures surface via the more-visible toast above. */}
      <ConfirmationModal
        show={confirmDisconnectOpen}
        close={toggleConfirmationModal}
        confirm={logout}
        loading={loading}
        message="Please note that any active campaigns will be paused in Facebook Ads Manager and stopped in Releva. WhatsApp message workflows will also permanently cease."
      />
    </Card>
  );
};

export default FacebookDisconnectButton;
